import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGames } from '../../Context/GameContext';
import { useQuestions } from '../../Context/QuestionContext';
import { useAuth } from '../../Context/AuthContext';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { ArrowLeft, Save, Trash2, Plus, Edit2, Play, Eye, X, AlertCircle, GripVertical } from 'lucide-react';
import { Container, Card, Form, Button, Row, Col, Table, Badge, Modal, Alert, Pagination } from 'react-bootstrap';
import QuestionFormModal from '../../Components/QuestionFormModal';
import ImageUpload from '../../Components/ImageUpload';

const GameDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { games, updateGame, setActiveGameId } = useGames();
  const { questions: allQuestions } = useQuestions();
  const { user } = useAuth();
  const isYetkili = user?.isYetkili;

  const game = games.find(g => g.id === Number(id));

  // --- HOOKS MUST BE UNCONDITIONAL ---
  const [formData, setFormData] = useState({
     title: '',
     description: '',
     image: '',
     is_private: false,
     // Bir oyuncunun bu yarışmada kullanabileceği TOPLAM joker sayısı. 0 = sınırsız.
     joker_limit: 0
  });
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [showPoolModal, setShowPoolModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]); 
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  
  
  useEffect(() => {
     if (game) {
         setFormData({
            title: game.title,
            description: game.description,
            image: game.image,
            is_private: game.is_private || false,
            joker_limit: Number(game.joker_limit) || 0
         });
        setSelectedQuestions(game.questions.map(q => q.id)); // Ensure ID array
     }
  }, [game]);

  // --- CONDITIONAL RENDERING AFTER HOOKS ---
  if (!game) {
     return <div className="p-5 text-center">Oyun bulunamadı. <Button variant="link" onClick={() => navigate('/games')}>Geri Dön</Button></div>;
  }

  const handleSave = async (e) => {
     e.preventDefault();
     
     let payload;
     if (selectedFile) {
         const data = new FormData();
         data.append('title', formData.title);
         data.append('description', formData.description);
         data.append('image', selectedFile);
         data.append('is_private', formData.is_private ? 'true' : 'false');
         data.append('joker_limit', String(formData.joker_limit ?? 0));
         data.append('questions', JSON.stringify(selectedQuestions));
         payload = data;
     } else {
        // If image was cleared (empty string) but no new file selected, we might want to signal removal.
        // For now, let's assume image URL update or no change.
        // If user cleared image in UI, formData.image is empty string.
        payload = { ...formData, questions: selectedQuestions };
        
        // If image is empty and we had one, meaningful to signal removal?
        if (!formData.image && game.image) {
            payload.removeImage = true;
        }
     }

     try {
        await updateGame(id, payload);
        const Swal = require('sweetalert2');
        Swal.fire({
            title: 'Güncellendi!',
            text: 'Oyun başarıyla güncellendi.',
            icon: 'success',
            confirmButtonText: 'Tamam'
        }).then(() => {
            navigate('/games');
        });
     } catch (error) {
        const Swal = require('sweetalert2');
        Swal.fire({
            title: 'Hata!',
            text: 'Oyun güncellenirken bir hata oluştu.',
            icon: 'error',
            confirmButtonText: 'Tamam'
        });
     }
  };

  const handleStartGame = () => {
     setActiveGameId(Number(id));
     navigate('/lobby');
  };

  const toggleQuestionSelection = (qId) => {
     if (selectedQuestions.includes(qId)) {
        setSelectedQuestions(selectedQuestions.filter(qid => qid !== qId));
     } else {
        setSelectedQuestions([...selectedQuestions, qId]);
     }
  };

  const openPreview = (q) => {
     setPreviewQuestion(q);
     setShowPreviewModal(true);
  };

  const handleNewQuestionSaved = (newQuestion) => {
     const Swal = require('sweetalert2');
     Swal.fire({
        title: 'Eklendi!',
        text: 'Soru havuza eklendi! Şimdi listeden seçebilirsiniz.',
        icon: 'success',
        confirmButtonText: 'Tamam'
     });
     setShowCreateModal(false);
     setShowPoolModal(true); 
  };

  const confirmDelete = (qId) => {
     const Swal = require('sweetalert2');
     Swal.fire({
        title: 'Emin misiniz?',
        text: "Bu soruyu oyundan çıkarmak istediğinize emin misiniz?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Evet, çıkar!',
        cancelButtonText: 'İptal'
     }).then((result) => {
        if (result.isConfirmed) {
            toggleQuestionSelection(qId);
            Swal.fire(
               'Çıkarıldı!',
               'Soru oyundan çıkarıldı.',
               'success'
            );
        }
     });
  };

  const gameQuestions = allQuestions
    .filter(q => selectedQuestions.includes(q.id))
    .sort((a, b) => selectedQuestions.indexOf(a.id) - selectedQuestions.indexOf(b.id));
    
  const availableQuestions = allQuestions; 

  const handleReorder = (newOrderIds) => {
    setSelectedQuestions(newOrderIds);
  };

  // Pagination logic
  const totalPages = Math.ceil(gameQuestions.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentQuestions = gameQuestions.slice(indexOfFirstItem, indexOfLastItem);


  return (
    <Container fluid className="py-2">
       <Button variant="link" onClick={() => navigate('/games')} className="text-decoration-none text-muted mb-3 p-0 d-flex align-items-center"><ArrowLeft size={18} className="me-2"/> Oyunlara Dön</Button>

       <Row className="g-4">
          <Col lg={4}>
             <Card className="custom-card border-0 mb-4 h-100">
                <Card.Header className="bg-white border-0 pt-4 px-4">
                   <h5 className="fw-bold mb-0 text-gradient">Oyun Ayarları</h5>
                </Card.Header>
                <Card.Body className="p-4">
                   <Form onSubmit={handleSave}>
                      <Form.Group className="mb-3">
                         <Form.Label className="small fw-bold text-secondary">OYUN BAŞLIĞI</Form.Label>
                         <Form.Control type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="shadow-none fw-bold" />
                      </Form.Group>
                      <Form.Group className="mb-3">
                         <Form.Label className="small fw-bold text-secondary">AÇIKLAMA</Form.Label>
                         <Form.Control as="textarea" rows={3} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="shadow-none" />
                      </Form.Group>
                      
                       {isYetkili && (
                          <Form.Group className="mb-3">
                             <Form.Label className="small fw-bold text-secondary">GÖRÜNÜRLÜK (VISIBILITY)</Form.Label>
                             <Form.Select
                                value={formData.is_private ? 'true' : 'false'}
                                onChange={(e) => setFormData({...formData, is_private: e.target.value === 'true'})}
                                className="shadow-none fw-bold"
                             >
                                <option value="false">Herkese Açık (Public)</option>
                                <option value="true">Gizli Yarışma (Sadece bende görünür)</option>
                             </Form.Select>
                          </Form.Group>
                       )}

                       <Form.Group className="mb-3">
                          <Form.Label className="small fw-bold text-secondary">JOKER HAKKI (BU YARIŞMADA)</Form.Label>
                          <Form.Select
                             value={String(formData.joker_limit ?? 0)}
                             onChange={(e) => setFormData({...formData, joker_limit: Number(e.target.value)})}
                             className="shadow-none fw-bold"
                          >
                             <option value="0">Sınırsız</option>
                             {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                <option key={n} value={String(n)}>{n} joker</option>
                             ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                             Bir oyuncunun tüm yarışma boyunca açabileceği toplam joker sayısı.
                             Yarı yarıya, çift cevap ve 2x puan aynı haktan düşer.
                          </Form.Text>
                       </Form.Group>

                       <ImageUpload 
                         value={formData.image} 
                         onChange={(url) => setFormData({...formData, image: url})} 
                         onFileChange={(file) => setSelectedFile(file)}
                       />
                      
                      <Button variant="primary" type="submit" className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2 mb-2">
                         <Save size={18} /> Değişiklikleri Kaydet
                      </Button>
                      <Button variant="success" className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2" onClick={handleStartGame}>
                         <Play fill="currentColor" size={18} /> Oyunu Başlat
                      </Button>
                   </Form>
                </Card.Body>
             </Card>
          </Col>

          <Col lg={8}>
             <Card className="custom-card border-0 h-100">
                <Card.Header className="bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                   <div>
                      <h5 className="fw-bold mb-0">Sorular</h5>
                      <p className="text-muted small mb-0">{gameQuestions.length} soru eklendi</p>
                   </div>
                   <div className="d-flex gap-2">
                      <Button variant="success" size="sm" onClick={() => setShowCreateModal(true)} className="d-flex align-items-center gap-2">
                         <Plus size={16} /> Yeni Oluştur
                      </Button>
                      <Button variant="outline-primary" size="sm" onClick={() => setShowPoolModal(true)} className="d-flex align-items-center gap-2">
                         <Plus size={16} /> Havuzdan Seç
                      </Button>
                   </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0 align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th className="border-0 ps-4 py-3 small text-secondary" style={{width: 50}}>#</th>
                                <th className="border-0 py-3 small text-secondary">Soru</th>
                                <th className="border-0 py-3 small text-secondary">Zorluk</th>
                                <th className="border-0 py-3 small text-secondary" style={{width: 80}}>Medya</th>
                                <th className="border-0 pe-4 py-3 text-end small text-secondary">İşlem</th>
                            </tr>
                        </thead>
                        <Reorder.Group 
                            as="tbody" 
                            axis="y" 
                            values={selectedQuestions} 
                            onReorder={handleReorder}
                        >
                            <AnimatePresence>
                            {gameQuestions.map((q, idx) => (
                                <Reorder.Item 
                                    key={q.id} 
                                    value={q.id}
                                    as="tr"
                                    initial={{opacity:0}} 
                                    animate={{opacity:1}} 
                                    exit={{opacity:0}}
                                    className="cursor-move"
                                >
                                <td className="ps-4">
                                    <div className="d-flex align-items-center gap-2">
                                        <GripVertical size={16} className="text-muted" />
                                        <span className="fw-bold text-muted">{idx + 1}</span>
                                    </div>
                                </td>
                                <td style={{maxWidth: 250}}>
                                    <div className="fw-medium text-truncate">{q.text}</div>
                                    <Badge bg={q.type === 'MULTIPLE_CHOICE' ? 'primary' : 'secondary'} className="mt-1" style={{fontSize: '0.7rem', fontWeight: 500}}>
                                        {q.type === 'MULTIPLE_CHOICE' ? 'Test Sorusu' : 'Klasik Soru'}
                                    </Badge>
                                </td>
                                <td>
                                    <Badge bg={q.difficulty === 'EASY' ? 'success' : q.difficulty === 'MEDIUM' ? 'warning' : 'danger'}>
                                        {q.difficulty}
                                    </Badge>
                                </td>
                                <td>
                                    {q.media && <Badge bg="info" className="text-white">Medya</Badge>}
                                </td>
                                <td className="text-end pe-4">
                                    <Button variant="light" size="sm" className="text-primary p-1 me-1" onClick={() => openPreview(q)} title="Önizle">
                                        <Eye size={16} />
                                    </Button>
                                    <Button variant="light" size="sm" className="text-danger p-1" onClick={() => confirmDelete(q.id)} title="Oyundan Çıkar">
                                        <Trash2 size={16} />
                                    </Button>
                                </td>
                                </Reorder.Item>
                            ))}
                            </AnimatePresence>
                        </Reorder.Group>
                      </Table>
                    </div>
                    {gameQuestions.length === 0 && (
                        <div className="text-center py-5 text-muted border-top">Bu oyunda henüz soru yok.</div>
                    )}

                </Card.Body>
                 {totalPages > 1 && (
                     <div className="d-flex justify-content-center p-3 border-top bg-white rounded-bottom">
                         <Pagination className="mb-0">
                             <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                             <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                             
                             {[...Array(totalPages)].map((_, idx) => (
                                 <Pagination.Item 
                                     key={idx + 1} 
                                     active={idx + 1 === currentPage}
                                     onClick={() => setCurrentPage(idx + 1)}
                                 >
                                     {idx + 1}
                                 </Pagination.Item>
                             ))}

                             <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                             <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                         </Pagination>
                     </div>
                 )}
              </Card>
           </Col>
        </Row>
        
        {/* POOL MODAL */}
       <Modal show={showPoolModal} onHide={() => setShowPoolModal(false)} size="xl" scrollable centered backdrop="static">
          <Modal.Header closeButton>
             <Modal.Title className="fw-bold">Soru Havuzu</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
             <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
                <span className="text-muted small">İstediğiniz soruları seçin.</span>
                <Button variant="link" size="sm" onClick={() => {setShowPoolModal(false); setShowCreateModal(true)}}>+ Aradığınız yok mu? Yeni oluşturun.</Button>
             </div>
             <Table hover className="mb-0">
                <tbody>
                   {availableQuestions.map(q => (
                      <tr key={q.id} onClick={() => toggleQuestionSelection(q.id)} className={`cursor-pointer ${selectedQuestions.includes(q.id) ? 'bg-primary bg-opacity-10' : ''}`}>
                         <td style={{width: 50}} className="text-center">
                            <Form.Check checked={selectedQuestions.includes(q.id)} readOnly style={{pointerEvents: 'none'}} />
                         </td>
                         <td>
                            <div className="fw-bold">{q.text}</div>
                            <div className="small text-muted d-flex gap-2">
                               <span>{q.difficulty}</span> • <span>{q.type}</span>
                               {q.media && <span className="text-info fw-bold">• Medyalı</span>}
                            </div>
                         </td>
                         <td className="text-end pe-3">
                            <Button size="sm" variant="light" onClick={(e) => {e.stopPropagation(); openPreview(q)}}><Eye size={14}/></Button>
                         </td>
                      </tr>
                   ))}
                </tbody>
             </Table>
          </Modal.Body>
          <Modal.Footer>
             <Button variant="primary" onClick={() => setShowPoolModal(false)}>Seçimi Tamamla</Button>
          </Modal.Footer>
       </Modal>

       {/* CREATE MODAL */}
       <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered backdrop="static">
          <Modal.Header closeButton>
             <Modal.Title className="fw-bold">Hızlı Soru Oluştur</Modal.Title>
          </Modal.Header>
          <Modal.Body>
             <Alert variant="info" className="py-2 small mb-3">
                <AlertCircle size={16} className="me-2"/>
                Pencereyi kapatmak için sağ üstteki <b>X</b> butonunu kullanınız. Dışarı tıklamak pencereyi kapatmaz.
             </Alert>
             <QuestionFormModal onClose={() => setShowCreateModal(false)} onSave={handleNewQuestionSaved} />
          </Modal.Body>
       </Modal>

       {/* PREVIEW MODAL */}
       <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} centered size="lg">
          <Modal.Body className="p-0 position-relative overflow-hidden rounded">
             <Button 
                variant="light" 
                className="position-absolute top-0 end-0 m-2 rounded-circle p-2 z-1 shadow-sm" 
                onClick={() => setShowPreviewModal(false)}
             >
                <X size={20}/>
             </Button>
             
             {previewQuestion && (
                <div className="text-center">
                   {previewQuestion.media && (
                      <div className="bg-dark text-white d-flex align-items-center justify-content-center" style={{minHeight: 300, maxHeight: 400}}>
                         {previewQuestion.media.type === 'IMAGE' ? (
                            <img src={previewQuestion.media.url} className="w-100 h-100 object-fit-contain" alt="Question" />
                         ) : (
                            <div>VIDEO: {previewQuestion.media.url}</div>
                         )}
                      </div>
                   )}
                   <div className="p-5 bg-white">
                      <Badge className="mb-3" bg={previewQuestion.difficulty === 'EASY' ? 'success' : 'warning'}>{previewQuestion.difficulty}</Badge>
                      <h3 className="fw-bold mb-4">{previewQuestion.text}</h3>
                      
                      {previewQuestion.type === 'MULTIPLE_CHOICE' ? (
                          <div className="d-grid gap-3 text-start">
                             {previewQuestion.options.map((opt, i) => {
                                 const isCorrect = (previewQuestion.correctAnswers && previewQuestion.correctAnswers.includes(opt)) || 
                                                   (previewQuestion.correctAnswer && previewQuestion.correctAnswer === opt);
                                 return (
                                   <div key={i} className={`p-3 border rounded ${isCorrect ? 'bg-success text-white border-success' : 'bg-light'}`}>
                                      <span className="fw-bold me-2">{['A','B','C','D'][i]})</span> {opt}
                                      {isCorrect && previewQuestion.correctAnswers && previewQuestion.correctAnswers.length > 1 && <span className="small ms-2 fw-normal">(Doğru)</span>}
                                   </div>
                                 );
                             })}
                             {previewQuestion.correctAnswers && previewQuestion.correctAnswers.length > 1 && (
                                 <div className="text-center text-secondary small mt-2 fst-italic">
                                     (Bu sorunun birden fazla doğru cevabı vardır)
                                 </div>
                             )}
                          </div>
                       ) : (
                          <div className="p-3 border rounded bg-success bg-opacity-10 text-success fw-bold">
                             Cevap: {Array.isArray(previewQuestion.correctAnswers) ? previewQuestion.correctAnswers.join(', ') : previewQuestion.correctAnswer}
                          </div>
                      )}
                   </div>
                </div>
             )}
          </Modal.Body>
       </Modal>
       


    </Container>
  );
};

export default GameDetail;
